import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import Button from '../common/Button';
import { Input } from '../common/Input';
import { FaLock } from 'react-icons/fa';
import { supabase } from '../../config/supabase';

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1rem;
`;

const StyledInput = styled(Input)`
  padding-left: 2.5rem;
  width: 100%;
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

const SuccessMessage = styled.div`
  color: ${({ theme }) => theme.colors.success};
  font-size: 0.9rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: ${({ theme }) => `${theme.colors.success}10`};
  border-radius: 4px;
  text-align: center;
`;

const Description = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: -0.5rem;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

interface ResetPasswordFormProps {
  onSuccess?: () => void;
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ onSuccess }) => {
  const { updatePassword: _updatePassword, error, loading, clearError } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ 
    password?: string;
    confirmPassword?: string;
  }>({});
  const [resetSuccess, setResetSuccess] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    const handleHashChange = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('type=recovery')) {
        try {
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('Erreur lors de la récupération de la session:', error);
            setLocalError('Impossible de récupérer la session. Veuillez réessayer ou demander un nouveau lien de réinitialisation.');
          } else if (!data.session) {
            setLocalError('Aucune session trouvée. Le lien de réinitialisation est peut-être expiré. Veuillez demander un nouveau lien.');
          } else {
            console.log('Session récupérée avec succès:', data.session);
            setLocalError(null);
          }
        } catch (err) {
          console.error('Exception lors de la récupération de la session:', err);
          setLocalError('Une erreur est survenue. Veuillez réessayer plus tard.');
        }
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const validateForm = (): boolean => {
    const errors: { 
      password?: string;
      confirmPassword?: string;
    } = {};
    
    if (!password) {
      errors.password = 'Le mot de passe est requis';
    } else if (password.length < 6) {
      errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = 'Veuillez confirmer votre mot de passe';
    } else if (confirmPassword !== password) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setResetSuccess(false);
    setLocalError(null);
    
    if (validateForm()) {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        
        if (!sessionData.session) {
          setLocalError('Aucune session active. Veuillez utiliser le lien de réinitialisation envoyé par email.');
          return;
        }
        
        const { error: updateError } = await supabase.auth.updateUser({
          password: password
        });
        
        if (updateError) {
          throw updateError;
        }
        
        setResetSuccess(true);
        setPassword('');
        setConfirmPassword('');
        
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 2000);
        }
      } catch (err: any) {
        console.error('Erreur lors de la réinitialisation du mot de passe:', err);
        setLocalError(err.message || 'Une erreur est survenue lors de la réinitialisation du mot de passe.');
      }
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit}>
      <h2>Réinitialiser le mot de passe</h2>
      <Description>
        Veuillez entrer votre nouveau mot de passe.
      </Description>
      
      <FormGroup>
        <Label htmlFor="password">Nouveau mot de passe</Label>
        <InputWrapper>
          <IconWrapper>
            <FaLock />
          </IconWrapper>
          <StyledInput
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Votre nouveau mot de passe"
            $hasError={!!validationErrors.password}
          />
        </InputWrapper>
        {validationErrors.password && <ErrorMessage>{validationErrors.password}</ErrorMessage>}
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
        <InputWrapper>
          <IconWrapper>
            <FaLock />
          </IconWrapper>
          <StyledInput
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirmez votre nouveau mot de passe"
            $hasError={!!validationErrors.confirmPassword}
          />
        </InputWrapper>
        {validationErrors.confirmPassword && <ErrorMessage>{validationErrors.confirmPassword}</ErrorMessage>}
      </FormGroup>
      
      {error && <ErrorMessage>{error.message}</ErrorMessage>}
      {localError && <ErrorMessage>{localError}</ErrorMessage>}
      
      {resetSuccess && (
        <SuccessMessage>
          Votre mot de passe a été réinitialisé avec succès !
        </SuccessMessage>
      )}
      
      <ButtonsContainer>
        <Button type="submit" $variant="primary" disabled={loading}>
          {loading ? 'Réinitialisation en cours...' : 'Réinitialiser le mot de passe'}
        </Button>
      </ButtonsContainer>
    </FormContainer>
  );
};

export default ResetPasswordForm;

